import {
	Card,
	CardBody,
	CardHeader,
	CardHeading,
	DetailsPageInfoField,
	FormattedDateTime,
	InfoIconTooltipPopover,
	Row,
	SlateViewer,
	isEmptySlateValue,
	useIsAddonEnabled,
} from "@dgs/core";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { EventDetailResource } from "../../types/event";
import { Address } from "../../ui/Address";

interface Props {
	event: EventDetailResource;
	variant: "outlined" | "elevated";
}

const GridCardBody = styled(CardBody)`
	display: grid;
	grid-gap: ${({ theme }) => theme.spacing(2)};
	grid-template-columns: 1fr;

	${({ theme }) => theme.breakpoints.tablet} {
		grid-template-columns: 1fr 1fr;
	}
`;

const CardWrapper = styled.div``;

export const EventOffboardingOverviewCard: FC<Props> = ({ event, variant }) => {
	const { t } = useTranslation();
	const isPressModuleEnabled = useIsAddonEnabled("pressModule");

	return (
		<CardWrapper>
			<Card $variant={variant}>
				<CardHeader>
					<CardHeading>{t("Overview")}</CardHeading>
				</CardHeader>
				<GridCardBody>
					<DetailsPageInfoField title={t("Name")} value={event.name} />
					<DetailsPageInfoField title={t("Event type")} value={event.eventType.name} />
					<DetailsPageInfoField title={t("Start")} value={<FormattedDateTime dateTime={event.start} />} />
					<DetailsPageInfoField title={t("End")} value={<FormattedDateTime dateTime={event.end} />} />
					<DetailsPageInfoField title={t("Timezone")} value={event.timezone} />
					<DetailsPageInfoField title={t("Primary language")} value={event.primaryLanguage?.name} />
					{event.eventType.eventLanguageRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Event languages")}
							value={event.languages.map((language) => language.name).join(", ")}
						/>
					)}
					{event.eventType.organizerRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Organiser")}
							value={
								event.organizer ? (
									<Row $columnGap={2}>
										{event.organizer.name}{" "}
										{event.organizer.description && !isEmptySlateValue(event.organizer.description) && (
											<InfoIconTooltipPopover>
												<SlateViewer content={event.organizer.description} />
											</InfoIconTooltipPopover>
										)}
									</Row>
								) : (
									"-"
								)
							}
						/>
					)}
					<DetailsPageInfoField title={t("Creator")} value={event.creator?.name ?? "-"} />
					<DetailsPageInfoField title={t("Visibility")} value={t(`eventVisibility.${event.visibility}`)} />
					{isPressModuleEnabled && <DetailsPageInfoField title={t("Clippings")} value={event.clippingCount || 0} />}
					{event.eventType.locationRequirement !== "prohibited" && (
						<DetailsPageInfoField title={t("Location")} value={event.location?.name || "-"} />
					)}
					{event.eventType.addressRequirement !== "prohibited" && (
						<DetailsPageInfoField
							title={t("Address")}
							value={event.address ? <Address address={event.address} /> : "-"}
						/>
					)}
				</GridCardBody>
			</Card>
		</CardWrapper>
	);
};
